import React, { useRef, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { func, shape } from 'prop-types';
import { Modal, Button, Segment } from 'semantic-ui-react';
import moment from 'moment';
import { getDigiType, createCert, resetCert } from 'Redux/modules/webChat/emc/actions';
import useMcHistory from 'Hooks/useMcHistory';
import useProfile from 'Hooks/doctor/useDoctorProfile';
import { findClinicIdFromClinicsMapping } from 'Helpers/consult-utils';
import useActiveClinicTab from 'Containers/Teleconsult/hooks/useActiveClinicTab';

import MCDigiForm from './MCDigiForm';
import { findDigiType } from './helpers';
import styles from './MCDigi.less';

const DATE_FORMAT = 'YYYY-MM-DD';

const MCDigi = ({ data }) => {
  const { onClose, conversation } = data;

  const {
    id: groupId,
    latestConsult: {
      id: consultId,
      patient: { id: patientId, phoneCountryCode },
      forPatient: { id: forPatientId, fullName, nric, dateOfBirth },
    },
  } = conversation;
  const dispatch = useDispatch();
  const doctorId = useSelector(state => state.webChat.doctor.data.id, shallowEqual);
  const digi = useSelector(state => state.webChat.emc.digi, shallowEqual);
  const { status, error } = useSelector(state => state.webChat.emc.certification, shallowEqual);
  const { activeClinicTab } = useActiveClinicTab();
  const { profile } = useProfile();

  const { types } = digi;

  const { getHistoryMc, getCountHistoryMc } = useMcHistory();

  const formRef = useRef();
  const handleSubmit = () => {
    formRef.current.handleSubmit();
  };
  const createDigi = async body => {
    await dispatch(createCert(body)); // NOSONAR
  };

  const onSubmit = async values => {
    const {
      digiType,
      days,
      fitday,
      fitFrom,
      fitTo,
      patientNric,
      patientName,
      isAnnex,
      startDate,
      clinicId,
      comments,
    } = values;
    const body = {
      patientId,
      forPatientId,
      consultId,
      serviceType: 'consult',
      isDigiMc: true,
      threadId: groupId,
      digiType,
      days: +days,
      patientNric,
      isAnnex,
      patientName,
      startDate: startDate.format(DATE_FORMAT),
      clinicId,
      patientDob: dateOfBirth ? moment.parseZone(dateOfBirth).format(DATE_FORMAT) : undefined,
      comments,
    };
    if (fitday) {
      body.fitFrom = fitFrom.format(DATE_FORMAT);
      body.fitTo = fitTo.format(DATE_FORMAT);
    }

    const selectedType = findDigiType(types, digiType);

    if (selectedType.fields.length > 0) {
      const { fields } = selectedType;
      fields.forEach(i => {
        body[i.key] =
          i.dataType === 'datetime'
            ? values[i.key].toISOString()
            : values[i.key].format(DATE_FORMAT);
      });
    }

    await createDigi(body);

    await getCountHistoryMc(
      forPatientId,
      activeClinicTab,
      conversation?.latestConsult?.service?.id,
    );
    await getHistoryMc(forPatientId, { clinicId: activeClinicTab });
  };

  useEffect(() => {
    if (status.creating === 1 || status.approving === 1) onClose();
  }, [status.approving, status.creating, onClose]);

  useEffect(() => {
    dispatch(getDigiType());
    return () => dispatch(resetCert());
  }, [dispatch, doctorId]);

  const initialValues = {
    patientName: fullName || '',
    patientNric: nric || '',
    isAnnex: false,
    fitday: false,
    days: 1,
    startDate: moment(),
    digiType: undefined,
    clinicId: findClinicIdFromClinicsMapping(
      conversation?.latestConsult?.clinics || [],
      profile?.id,
    ),
    fitFrom: undefined,
    fitTo: undefined,
    comments: '',
  };

  const submitting = status.creating === 0 || status.approving === 0;
  const isError = status.creating === 2 || status.approving === 2;

  const isBookForSelf = patientId === forPatientId;
  const isSingaporeCitizen = phoneCountryCode === '+65';
  const isNeedValidationAge = isBookForSelf && isSingaporeCitizen;

  return (
    <Modal open closeIcon onClose={onClose} closeOnDimmerClick={false}>
      <Modal.Header>Create Medical Certificate</Modal.Header>
      <Modal.Content>
        {status.loading === 0 ? (
          <Segment basic loading style={{ minHeight: 400 }} />
        ) : (
          <MCDigiForm
            ref={formRef}
            onSubmit={onSubmit}
            types={types}
            initialValues={initialValues}
            isNeedValidationAge={isNeedValidationAge}
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <div className={styles.modalActions}>
          {isError && error ? <span className={styles.errorText}>{error}</span> : <div />}
          <div>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              primary
              type="submit"
              loading={submitting}
              disabled={submitting}>
              Create
            </Button>
          </div>
        </div>
      </Modal.Actions>
    </Modal>
  );
};

MCDigi.propTypes = {
  data: shape({
    onClose: func.isRequired,
    conversation: shape({}).isRequired,
  }),
};

export default MCDigi;
